import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSelectedRestaurant } from '../../context/SelectedRestaurantContext';
import { tablesService } from '../../services/tables';
import InlineAlert from '../../components/InlineAlert';
import RestaurantSelector from '../../components/RestaurantSelector';
import WaiterShiftIndicator from '../../components/WaiterShiftIndicator';

type Table = {
  id: string;
  number: number;
  capacity: number;
  location: string;
  status: string; // active | inactive | maintenance
};

export default function WaiterTablesPage() {
  const { user } = useAuth();
  const { selectedRestaurantId, selectedRestaurantName } = useSelectedRestaurant();
  const navigate = useNavigate();

  const [tables, setTables] = useState<Table[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [qrModal, setQrModal] = useState<{ open: boolean; table?: Table; qrUrl?: string; linkUrl?: string }>({ open: false });
  const [isChanging, setIsChanging] = useState<string | null>(null);

  const appRoot = useMemo(() => import.meta.env.VITE_APP_ROOT_URL || window.location.origin, []);

  const loadTables = async () => {
    if (!user?.uid || !selectedRestaurantId) return;
    try {
      setLoading(true);
      setError(null);
      const response: any = await tablesService.getTables(user.uid, selectedRestaurantId);
      const data = Array.isArray(response?.data)
        ? response.data
        : (Array.isArray(response) ? response : response?.tables || []);
      setTables(data || []);
    } catch (e: any) {
      setError(e?.message || 'No se pudieron cargar las mesas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, selectedRestaurantId]);

  const toggleOccupied = async (table: Table) => {
    if (!user?.uid || !selectedRestaurantId) return;
    const isActive = (table.status || '').toLowerCase() === 'active';
    const next = isActive ? 'occupied' : 'active';
    try {
      setIsChanging(table.id);
      await tablesService.changeTableStatus(user.uid, selectedRestaurantId, table.id, next);
      setTables(prev => prev.map(t => t.id === table.id ? { ...t, status: next } : t));
    } catch (e: any) {
      setError(e?.message || 'No se pudo cambiar el estado de la mesa');
    } finally {
      setIsChanging(null);
    }
  };

  const openQrForTable = async (table: Table) => {
    if (!user?.uid || !selectedRestaurantId) return;
    try {
      const tokenResponse = await tablesService.getTableTokenByTableId(user.uid, selectedRestaurantId, table.id);
      // El admin usa: base64Token = btoa(JSON.stringify({ token_mesa: response }))
      const base64Token = btoa(JSON.stringify({ token_mesa: tokenResponse }));
      const linkUrl = `${appRoot}/menu?table=${base64Token}`;
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(linkUrl)}&format=png&margin=10&color=000000&bgcolor=FFFFFF`;
      setQrModal({ open: true, table, qrUrl, linkUrl });
    } catch (e: any) {
      setError(e?.message || 'No se pudo generar el QR de la mesa');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#2C4358]">
        Debes iniciar sesión.
      </div>
    );
  }

  if (!selectedRestaurantId) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#2C4358] px-4 text-center">
        Selecciona un restaurante en el header para ver tus mesas.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#e9ecef]">
      {/* Header consistente con botón back y selector */}
      <div className="bg-[#22B6B2] dark:bg-[#2C4358] px-4 py-3 sticky top-0 z-30">
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-white hover:text-white/80 hover:bg-white/10 rounded-lg transition-colors text-2xl"
            aria-label="Regresar"
            title="Regresar"
          >
            ←
          </button>
          <div className="flex-1">
            <RestaurantSelector isHeader={true} />
          </div>
          <WaiterShiftIndicator isHeader={true} />
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#2C4358]">Mesas</h1>
            <p className="text-[#85929e] text-sm">{selectedRestaurantName || ''}</p>
          </div>
          <button onClick={loadTables} className="px-3 py-2 rounded-xl border border-gray-200 text-[#2C4358] hover:bg-gray-50">
            Recargar
          </button>
        </div>

        {error && <div className="mb-4"><InlineAlert type="error" message={error} onClose={() => setError(null)} /></div>}

        {loading ? (
          <div className="text-center text-[#2C4358]">Cargando mesas...</div>
        ) : tables.length === 0 ? (
          <div className="text-center text-[#85929e]">No hay mesas</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {tables.map((table) => {
              const statusLower = (table.status || '').toLowerCase();
              const isActive = statusLower === 'active';
              const isOccupied = statusLower === 'occupied';
              return (
                <div key={table.id} className="bg-white rounded-2xl shadow-md border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xl font-bold text-[#2C4358]">Mesa {table.number}</div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${isActive ? 'bg-green-100 text-green-800' : isOccupied ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-800'}`}>
                      {isActive ? 'Libre' : isOccupied ? 'Ocupada' : (table.status || '—')}
                    </span>
                  </div>
                  <div className="text-sm text-[#85929e] mb-3">Capacidad: {table.capacity} • {table.location}</div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleOccupied(table)}
                      disabled={isChanging === table.id}
                      className={`flex-1 px-3 py-2 rounded-xl font-semibold transition-colors border-2 ${
                        isActive
                          ? 'border-orange-300 text-orange-700 hover:bg-orange-50'
                          : 'border-green-300 text-green-700 hover:bg-green-50'
                      } ${isChanging === table.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isActive ? 'Marcar Ocupada' : 'Marcar Libre'}
                    </button>
                    <button
                      onClick={() => openQrForTable(table)}
                      className="px-3 py-2 rounded-xl bg-[#22B6B2] text-white font-semibold hover:opacity-90"
                    >
                      Ver Menú
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {qrModal.open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-[#2C4358]">Mesa {qrModal.table?.number}</h3>
              <button onClick={() => setQrModal({ open: false })} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <div className="text-center mb-4">
              {qrModal.qrUrl && <img src={qrModal.qrUrl} alt="QR" className="mx-auto rounded-xl shadow" />}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <a href={qrModal.linkUrl} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto px-4 py-3 rounded-2xl bg-[#22B6B2] text-white font-semibold text-center hover:opacity-90">Abrir Menú</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


